import { useMemo, useState } from "react";
import { Link } from "react-router";
import {
  Alert,
  Badge,
  Button,
  Card,
  EmptyState,
  Field,
  Input,
  Modal,
  SearchInput,
  Switch,
  Table,
  type TableColumn,
} from "../../ds";
import type { WgOverview, WgPeerInput, WgServerPeer } from "../../lib/api";
import { fmtBytes, relSeconds } from "../../lib/format";
import { clientConfig, generateKeyPair, nextFreeAddress } from "../../lib/wgclient";
import { CopyOnClick, CopyValue } from "../CopyValue";
import { WgClientConfig } from "./WgClientConfig";

// Wen der Server hereinlässt — der Bestand, aus dem `peers.conf` entsteht.
//
// Das Gegenstück zur Uplink-Tabelle: dort steht, was die Router über ihren
// Tunnel glauben, hier, was der Server tatsächlich sieht. Ein Router, der oben
// „Handshake veraltet" meldet und hier gar nicht auftaucht, hat kein
// Netzproblem, sondern fehlt im Bestand.

/** Ab wann ein Handshake auffällig alt ist (WG erneuert alle ~2 min). */
const STALE_HANDSHAKE_SECS = 300;

/** Leeres Formular für „Peer aufnehmen". */
const LEER: WgPeerInput = { name: "", publicKey: "", address: "", routes: [], keepalive: 0 };

interface Props {
  data: WgOverview;
  onSave: (id: string | null, peer: WgPeerInput) => Promise<void>;
  onDelete: (peer: WgServerPeer) => Promise<void>;
  busy: boolean;
}

function HandshakeZelle({ peer }: { peer: WgServerPeer }) {
  if (!peer.enabled) return <Badge variant="neutral">aus</Badge>;
  if (!peer.onServer) {
    // Im Bestand, aber nicht am laufenden Interface: peers.conf ist
    // geschrieben, der Sync-Dienst hat sie noch nicht angewandt.
    return <Badge variant="warn">nicht am Server</Badge>;
  }
  const secs = peer.latestHandshakeSecs;
  const alt = secs === null || secs > STALE_HANDSHAKE_SECS;
  return <Badge variant={alt ? "warn" : "success"}>{relSeconds(secs)}</Badge>;
}

function spalten(
  onEdit: (peer: WgServerPeer) => void,
  onDelete: (peer: WgServerPeer) => void,
  onToggle: (peer: WgServerPeer) => void,
  busy: boolean,
): TableColumn<WgServerPeer>[] {
  return [
    { key: "name", header: "Name", cell: (p) => p.name || p.publicKey.slice(0, 12) },
    {
      key: "addr",
      header: "Adresse",
      mono: true,
      // Kopiert wird die nackte Adresse: das /32 steht hier, damit man sieht,
      // dass der Peer genau einen Host beansprucht — eingetippt wird es nie.
      cell: (p) => <CopyOnClick value={p.address} copy={p.address.replace(/\/\d+$/, "")} />,
    },
    {
      key: "routes",
      header: "Netze dahinter",
      mono: true,
      // Der eigentliche Zweck des Tunnels: nicht der Router ist das Ziel,
      // sondern das LAN an seinem LAN-Port. Ohne diese Spalte steht die
      // Information zwar im Bestand, aber nur im Bearbeiten-Dialog — man sieht
      // also nicht, WO ein Netz hingeht, ohne 30 Dialoge zu öffnen.
      // Jedes Netz einzeln klickbar, sonst kopiert ein Klick bei zwei Netzen
      // beide auf einmal. Hier MIT Maske: ein Netz ohne Präfix ist keins.
      cell: (p) =>
        p.routes.length === 0 ? (
          <span className="text-content-3">—</span>
        ) : (
          <span className="inline-flex flex-wrap gap-x-1">
            {p.routes.map((route, i) => (
              <span key={route}>
                <CopyOnClick value={route} />
                {i < p.routes.length - 1 ? "," : ""}
              </span>
            ))}
          </span>
        ),
    },
    {
      key: "device",
      header: "Router",
      hideOnMobile: true,
      // Von hier aus will man weiter: der Peer sagt, dass etwas klemmt, die
      // Detailseite sagt warum.
      cell: (p) =>
        p.deviceId ? (
          <Link to={`/fleet/${p.deviceId}`} className="ds-link">
            {p.deviceHostname ?? p.deviceId}
          </Link>
        ) : (
          <span className="text-content-3">—</span>
        ),
    },
    {
      key: "psk",
      header: "PSK",
      hideOnMobile: true,
      // Ohne diese Spalte ist ein fehlender Preshared Key unsichtbar — und
      // genau das war die Ursache, dass ein Zugang nach dem Umzug still nicht
      // mehr ging.
      cell: (p) =>
        p.hasPresharedKey ? (
          <Badge variant="success">gesetzt</Badge>
        ) : (
          <span className="text-content-3">—</span>
        ),
    },
    { key: "handshake", header: "Handshake", cell: (p) => <HandshakeZelle peer={p} /> },
    {
      key: "endpoint",
      header: "Endpoint",
      mono: true,
      hideOnMobile: true,
      cell: (p) => p.endpoint ?? "—",
    },
    {
      key: "traffic",
      header: "rx / tx",
      hideOnMobile: true,
      cell: (p) => `${fmtBytes(p.rxBytes)} / ${fmtBytes(p.txBytes)}`,
    },
    {
      key: "actions",
      header: "",
      cell: (p) => (
        <div className="flex justify-end gap-1">
          <Button
            size="sm"
            variant={p.enabled ? "ghost" : "success"}
            iconName={p.enabled ? "pause" : "play"}
            disabled={busy}
            // Kein Bestätigungsdialog: „Aus" ist folgenlos umkehrbar — der
            // Eintrag bleibt stehen, nur der Zugang ist zu. Das Entfernen
            // dagegen fragt nach, weil es den Schlüssel wegwirft.
            title={p.enabled ? "Zugang schließen — bleibt im Bestand" : "Zugang wieder öffnen"}
            onClick={() => onToggle(p)}
          >
            {p.enabled ? "Deaktivieren" : "Aktivieren"}
          </Button>
          <Button size="sm" variant="ghost" iconName="edit" onClick={() => onEdit(p)}>
            Bearbeiten
          </Button>
          <Button size="sm" variant="danger" iconName="trash" onClick={() => onDelete(p)}>
            Entfernen
          </Button>
        </div>
      ),
    },
  ];
}

export function WgPeerTable({ data, onSave, onDelete, busy }: Props) {
  const [form, setForm] = useState<{ id: string | null; werte: WgPeerInput } | null>(null);
  const [loeschen, setLoeschen] = useState<WgServerPeer | null>(null);
  const [suche, setSuche] = useState("");

  // Gesucht wird über den Bestand, nicht über die Messung: Name, Schlüssel,
  // Adresse, Netze und der Router dahinter. Nach einem Handshake-Alter zu
  // suchen ergäbe keinen Sinn, es ändert sich unter der Eingabe.
  const zeilen = useMemo(() => {
    const q = suche.trim().toLowerCase();
    if (!q) return data.peers;
    return data.peers.filter((p) =>
      [p.name, p.publicKey, p.address, p.deviceHostname, p.deviceId, p.note, ...p.routes]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q)),
    );
  }, [data.peers, suche]);

  const oeffnen = (peer: WgServerPeer) =>
    setForm({
      id: peer.id,
      werte: {
        name: peer.name,
        publicKey: peer.publicKey,
        address: peer.address,
        routes: peer.routes,
        keepalive: peer.keepalive,
        // Bewusst NICHT vorbefüllt: der Server gibt ihn nicht heraus, ein
        // Platzhalter wäre eine Behauptung.
        enabled: peer.enabled,
        note: peer.note,
      },
    });

  const setzen = (teil: Partial<WgPeerInput>) =>
    setForm((alt) => (alt ? { ...alt, werte: { ...alt.werte, ...teil } } : alt));

  const umschalten = (peer: WgServerPeer) => void onSave(peer.id, { enabled: !peer.enabled });

  return (
    <>
      <Card
        title="Peers am Server"
        subtitle="Der Bestand, aus dem die Server-Konfiguration entsteht."
        bodyPadding={false}
        actions={
          <>
            {data.peers.length > 0 && (
              <SearchInput
                value={suche}
                onChange={setSuche}
                placeholder="Name, Adresse, Netz, Router …"
              />
            )}
            <Button
              size="sm"
              variant="primary"
              iconName="plus"
              onClick={() => setForm({ id: null, werte: { ...LEER } })}
            >
              Peer anlegen
            </Button>
          </>
        }
      >
        {data.peers.length === 0 ? (
          <EmptyState
            iconName="shield"
            title="Kein Peer im Bestand"
            description="Der Bestand kommt aus der Konfiguration des Konzentrators: manage.py wg_import. Einzelne Zugänge über „Peer anlegen“."
          />
        ) : (
          <Table
            columns={spalten(oeffnen, setLoeschen, umschalten, busy)}
            rows={zeilen}
            rowKey={(p) => p.id}
            emptyMessage="Kein Peer passt zur Suche."
          />
        )}
      </Card>

      {data.unknown.length > 0 && (
        <Card title="Unbekannte Schlüssel" bodyPadding={false}>
          <div className="p-4">
            <Alert variant="warning">
              Diese Schlüssel lässt der Server herein, ohne dass sie im Bestand stehen. Entweder
              gehören sie zu jemandem, der hier fehlt — oder es ist ein vergessener Zugang. Der
              nächste Sync entfernt sie: <code>peers.conf</code> ist die Wahrheit.
            </Alert>
          </div>
          <Table
            columns={[
              {
                key: "key",
                header: "Public Key",
                cell: (u) => <CopyValue value={u.publicKey} />,
              },
              {
                key: "allowed",
                header: "AllowedIPs",
                mono: true,
                cell: (u) => (u.allowedIps ?? []).join(", ") || "—",
              },
              {
                key: "handshake",
                header: "Handshake",
                cell: (u) => relSeconds(u.latestHandshakeSecs),
              },
            ]}
            rows={data.unknown}
            rowKey={(u) => u.publicKey}
          />
        </Card>
      )}

      <PeerModal
        form={form}
        setzen={setzen}
        onClose={() => setForm(null)}
        onSave={onSave}
        busy={busy}
        data={data}
      />

      <DeleteModal
        peer={loeschen}
        onClose={() => setLoeschen(null)}
        onDelete={onDelete}
        busy={busy}
      />
    </>
  );
}

/** Peer anlegen oder ändern. Eigene Komponente, damit `WgPeerTable` die Tabelle
 *  bleibt und nicht zur Sammelstelle für alles wird, was daneben aufgeht. */
function PeerModal({
  form,
  setzen,
  onClose,
  onSave,
  busy,
  data,
}: {
  form: { id: string | null; werte: WgPeerInput } | null;
  setzen: (teil: Partial<WgPeerInput>) => void;
  onClose: () => void;
  onSave: (id: string | null, peer: WgPeerInput) => Promise<void>;
  busy: boolean;
  data: WgOverview;
}) {
  // Der private Schlüssel des erzeugten Clients — NUR hier, in einem State, der
  // mit dem Dialog stirbt. Er geht nie in `onSave` und damit nie zum Server.
  const [privat, setPrivat] = useState<string | null>(null);
  const [psk, setPsk] = useState<string | null>(null);
  const [fertig, setFertig] = useState<string | null>(null);
  const [krypto, setKrypto] = useState<string | null>(null);

  const schliessen = () => {
    setPrivat(null);
    setPsk(null);
    setFertig(null);
    setKrypto(null);
    onClose();
  };

  const erzeugen = () => {
    setKrypto(null);
    void generateKeyPair().then(
      (paar) => {
        setPrivat(paar.privateKey);
        setPsk(paar.presharedKey);
        setzen({
          publicKey: paar.publicKey,
          // Der PSK geht MIT zum Server: er ist symmetrisch, beide Seiten
          // brauchen ihn. Nur der private Schlüssel bleibt hier.
          presharedKey: paar.presharedKey,
          address: form?.werte.address || nextFreeAddress(data.peers, data.server.address),
        });
      },
      (e: unknown) => setKrypto(e instanceof Error ? e.message : String(e)),
    );
  };

  // Nach dem Speichern eines selbst erzeugten Schlüssels: die Konfiguration
  // zeigen, statt den Dialog zuzuklappen. Sie ist danach nicht wiederholbar.
  if (fertig !== null) {
    return (
      <ErgebnisModal name={form?.werte.name || "eulektro"} config={fertig} onClose={schliessen} />
    );
  }

  return (
    <Modal
      open={form !== null}
      onClose={schliessen}
      title={form?.id ? "Peer bearbeiten" : "Peer anlegen"}
      subtitle="Gilt sofort: gespeichert heißt hier auch angewandt."
      actions={
        <>
          <Button variant="ghost" onClick={schliessen}>
            Abbrechen
          </Button>
          <Button
            variant="primary"
            iconName="check"
            disabled={busy}
            onClick={() => {
              if (!form) return;
              // Leeres PSK-Feld beim BEARBEITEN heißt „unverändert", nicht
              // „löschen". Sonst nähme jedes Speichern eines Peers seinen
              // Preshared Key mit — und der Tunnel bräche danach ohne jede
              // Meldung, weil die Gegenseite ihn weiter mitschickt.
              const werte = { ...form.werte };
              if (form.id !== null && !werte.presharedKey) delete werte.presharedKey;
              void onSave(form.id, werte).then(() => {
                // Ohne selbst erzeugten Schlüssel gibt es nichts auszugeben —
                // dann ist Speichern das Ende des Vorgangs wie bisher.
                if (privat === null) return schliessen();
                setFertig(
                  clientConfig({
                    name: werte.name ?? "",
                    privateKey: privat,
                    presharedKey: psk ?? undefined,
                    address: werte.address ?? "",
                    data,
                  }),
                );
              });
            }}
          >
            Speichern
          </Button>
        </>
      }
    >
      {form && (
        <PeerFelder
          form={form}
          setzen={setzen}
          privat={privat}
          setPrivat={setPrivat}
          erzeugen={erzeugen}
          krypto={krypto}
        />
      )}
    </Modal>
  );
}

/** Der Zugang geht damit sofort zu — deshalb ein eigener Schritt, kein `confirm()`. */
function DeleteModal({
  peer,
  onClose,
  onDelete,
  busy,
}: {
  peer: WgServerPeer | null;
  onClose: () => void;
  onDelete: (peer: WgServerPeer) => Promise<void>;
  busy: boolean;
}) {
  return (
    <Modal
      open={peer !== null}
      onClose={() => onClose()}
      title="Peer entfernen"
      actions={
        <>
          <Button variant="ghost" onClick={() => onClose()}>
            Abbrechen
          </Button>
          <Button
            variant="danger"
            iconName="trash"
            disabled={busy}
            onClick={() => {
              if (!peer) return;
              void onDelete(peer).then(() => onClose());
            }}
          >
            Entfernen
          </Button>
        </>
      }
    >
      <Alert variant="warning">
        <strong>{peer?.name}</strong> ({peer?.address}) kommt danach nicht mehr herein — sofort,
        nicht erst beim nächsten Neustart. Bei einem Router heißt das: kein Fernzugriff mehr, bis
        sein Schlüssel wieder im Bestand steht.
      </Alert>
    </Modal>
  );
}

/** Was nach dem Anlegen genau einmal zu sehen ist. Eigener Dialog, weil er
 *  nichts mit dem Formular zu tun hat außer dem Zeitpunkt. */
function ErgebnisModal({
  name,
  config,
  onClose,
}: {
  name: string;
  config: string;
  onClose: () => void;
}) {
  return (
    <Modal
      open
      onClose={onClose}
      title="Zugang angelegt"
      subtitle="Einmalig — der private Schlüssel ist nur in diesem Fenster."
      actions={
        <Button variant="primary" iconName="check" onClick={onClose}>
          Fertig
        </Button>
      }
    >
      <WgClientConfig name={name} config={config} />
    </Modal>
  );
}

/** Die Eingabefelder. Ausgelagert, damit `PeerModal` der Dialog bleibt und
 *  nicht zugleich das Formular ist. */
function PeerFelder({
  form,
  setzen,
  privat,
  setPrivat,
  erzeugen,
  krypto,
}: {
  form: { id: string | null; werte: WgPeerInput };
  setzen: (teil: Partial<WgPeerInput>) => void;
  privat: string | null;
  setPrivat: (wert: string | null) => void;
  erzeugen: () => void;
  krypto: string | null;
}) {
  return (
    <div className="flex flex-col gap-3">
      <Field label="Name" helperText="wofür der Zugang ist — steht als Kommentar in peers.conf">
        <Input value={form.werte.name ?? ""} onChange={(e) => setzen({ name: e.target.value })} />
      </Field>
      {form.id === null && (
        <Alert variant="neutral">
          Für einen neuen Client kann der Browser das Schlüsselpaar erzeugen. Der{" "}
          <strong>private</strong> Teil bleibt dann hier und geht nie an den Server — Spyship
          bekommt nur den öffentlichen. Nach dem Speichern gibt es Konfiguration und QR-Code, genau
          einmal.
          <div className="mt-2">
            <Button size="sm" variant="outline" iconName="key" onClick={erzeugen}>
              Schlüsselpaar erzeugen
            </Button>
          </div>
          {krypto && <div className="mt-2 text-danger">{krypto}</div>}
        </Alert>
      )}
      <Field
        label="Public Key"
        helperText={
          privat
            ? "im Browser erzeugt — der private Teil verlässt diese Seite nicht"
            : "43 Zeichen plus „=“"
        }
      >
        <Input
          value={form.werte.publicKey ?? ""}
          // Ein rotierter Schlüssel wäre ein anderer Peer, kein geänderter:
          // der alte Zugang bliebe sonst namenlos im Bestand zurück.
          disabled={form.id !== null}
          onChange={(e) => {
            // Von Hand überschrieben heißt: das erzeugte Paar gilt nicht
            // mehr. Sonst käme am Ende eine Konfiguration heraus, deren
            // privater Schlüssel nicht zum eingetragenen öffentlichen passt.
            setPrivat(null);
            setzen({ publicKey: e.target.value });
          }}
        />
      </Field>
      <Field label="Adresse im Tunnel" helperText="mit Präfix, z.B. 10.88.3.11/32">
        <Input
          value={form.werte.address ?? ""}
          onChange={(e) => setzen({ address: e.target.value })}
        />
      </Field>
      <Field
        label="Netze dahinter"
        helperText="kommagetrennt, leer lassen wenn nichts dahinter liegt — z.B. 10.88.7.0/24"
      >
        <Input
          value={(form.werte.routes ?? []).join(", ")}
          onChange={(e) =>
            setzen({
              routes: e.target.value
                .split(",")
                .map((s) => s.trim())
                .filter(Boolean),
            })
          }
        />
      </Field>
      <Field
        label="Keepalive"
        helperText="serverseitig meist 0 — den Tunnel hält der Client hinter dem NAT offen"
      >
        <Input
          type="number"
          value={String(form.werte.keepalive ?? 0)}
          onChange={(e) => setzen({ keepalive: Number(e.target.value) })}
        />
      </Field>
      {form.id !== null && (
        <Field helperText="Aus heißt: bleibt im Bestand, kommt aber nicht mehr herein.">
          <Switch
            checked={form.werte.enabled ?? true}
            onChange={(v) => setzen({ enabled: v })}
            label="Aktiv"
          />
        </Field>
      )}
    </div>
  );
}
